import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import NavBar from "../common/NavBar";
import Footer from "../common/Footer";
import { useTranslation } from "react-i18next";

const HomePage = () => {
  console.log("Environment frontend:", process.env.REACT_APP_ENV); // Log the environment variable
  const { t } = useTranslation();
  const [locations, setLocations] = useState([]); // State for locations
  const [location, setLocation] = useState(""); // Selected location
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [apartmentImages, setApartmentImages] = useState([]); // State for images
  const [error, setError] = useState("");
  const [paypalLoaded, setPaypalLoaded] = useState(false); // State to track PayPal SDK loading
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);


  const navigate = useNavigate();

  // Determine the API base URL based on the environment
  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_URL || "http://localhost:8000";
  };

  // Dynamically load PayPal SDK
  useEffect(() => {
    const loadPaypalSdk = () => {
      const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
      if (!clientId) {
        console.error("PayPal Client ID is not set in the environment variables.");
        return;
      }

      const script = document.createElement("script");
      script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=EUR`;
      script.async = true;
      script.onload = () => {
        console.log("PayPal SDK loaded successfully.");
        setPaypalLoaded(true);
      };
      script.onerror = () => {
        console.error("Failed to load PayPal SDK.");
        setError(t("failed_to_load_paypal_sdk"));
      };

      document.body.appendChild(script);
    };

    loadPaypalSdk();
  }, [t]);

  // Fetch locations from the API
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        console.log(`${getApiBaseUrl()}/api/locations/`);
        const response = await fetch(`${getApiBaseUrl()}/api/locations/`);
        if (!response.ok) {
          throw new Error("Failed to fetch locations");
        }

        const data = await response.json();
        setLocations(data); // Set the locations
        if (data.length > 0) {
          setLocation(data[0].id); // Set the first location as default
        }
      } catch (error) {
        if (process.env.NODE_ENV !== "production") {
          console.error("Error fetching locations:", error);
        }
        setLocations([]); // Set locations to an empty array on error
      }
    };

    fetchLocations();
  }, []);

  // Fetch images from the API
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/api/pictures/`);
        if (!response.ok) {
          throw new Error("Failed to fetch images");
        }

        const data = await response.json();

        // Extract image URLs from API response
        const images = data.map((item) => item.image);
        setApartmentImages(images);
      } catch (error) {
        if (process.env.NODE_ENV !== "production") {
          console.error("Error fetching images:", error);
        }
        setError(t("failed_to_load_images"));
      }
    };

    fetchImages();
  }, [t]);

  const handleSearch = async () => {
    try {
      // Step 1: Use the location ID to search for apartments
      const queryParams = new URLSearchParams({
        location, // Use location ID
        min_price: minPrice,
        max_price: maxPrice,
        start_date: startDate,
        end_date: endDate,
      }).toString();

      const apartmentResponse = await fetch(`${getApiBaseUrl()}/api/properties/apartments/filter?${queryParams}`);
      if (!apartmentResponse.ok) {
        throw new Error("Failed to fetch filtered apartments");
      }

      const apartmentData = await apartmentResponse.json();

      // Step 2: Redirect to Booking.js with results
      if (apartmentData.length > 0) {
        navigate("/booking", {
          state: {
            filteredApartments: apartmentData,
            startDate, // Pass startDate
            endDate, // Pass endDate
            adults,
            children,
          },
        });
      } else {
        alert("No apartments found matching your criteria.");
      }
    } catch (error) {
      console.error("Error during search:", error);
      setError("Failed to fetch apartments. Please try again later.");
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <NavBar />

      <h1 className="text-2xl md:text-3xl font-bold text-center mt-20 md:mt-4 px-4">{t("welcome")}</h1>

      {/* Search Bar */}
      <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-4 mt-6 md:mt-10 px-4">
        {/* Dropdown for location */}
        <select
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          className="border-2 border-gold w-full md:w-[50mm] h-12 md:h-[15mm] px-2 text-center"
          disabled={locations.length === 0}
        >
          {locations.length > 0 ? (
            locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))
          ) : (
            <option>{t("no_location_available")}</option>
          )}
        </select>

        {/* Adults & Children row */}
        <div className="flex gap-3 w-full md:w-auto">
        <div className="relative border-2 border-gold w-1/2 md:w-[15mm] h-12 md:h-[15mm] flex items-center justify-center">
          <label className="absolute top-0 left-1 text-[10px] text-gray-600">
            {t("adults")}
          </label>
          <input
            type="number"
            min="1"
            max="4"
            value={adults}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (val + children <= 4) setAdults(val);
            }}
            className="w-full h-full text-center pt-3 focus:outline-none"
          />
        </div>

        <div className="relative border-2 border-gold w-1/2 md:w-[15mm] h-12 md:h-[15mm] flex items-center justify-center">
          <label className="absolute top-0 left-1 text-[10px] text-gray-600">
            {t("children")}
          </label>
          <input
            type="number"
            min="0"
            max="3"
            value={children}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (val + adults <= 4) setChildren(val);
            }}
            className="w-full h-full text-center pt-3 focus:outline-none"
          />
        </div>
        </div>

        {[
          { placeholder: t("min_price"), value: minPrice, setValue: setMinPrice },
          { placeholder: t("max_price"), value: maxPrice, setValue: setMaxPrice },
          { 
            placeholder: t("start_date"),
            value: startDate,
            setValue: setStartDate,
            type: "date",
            min: new Date().toISOString().split("T")[0],
            max: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split("T")[0],
          },
          { 
            placeholder: t("end_date"),
            value: endDate,
            setValue: setEndDate,
            type: "date",
            min: new Date().toISOString().split("T")[0],
            max: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split("T")[0],
          },
        ].map(({ placeholder, value, setValue, type = "text", min, max }, index) => (
          <input
            key={index}
            type={type}
            placeholder={placeholder}
            min={min}
            max={max}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="border-2 border-gold w-full md:w-[40mm] h-12 md:h-[15mm] px-2 text-center"
          />
        ))}
        <button
          onClick={handleSearch}
          className="w-full md:w-[50mm] h-12 md:h-[15mm] bg-red-500 text-white font-bold border-2 border-gold rounded"
        >
          {t("search")}
        </button>
      </div>

{/* Apartment Carousel */}
<div className="w-[90%] md:w-3/4 h-[35vh] md:h-[50vh] mx-auto mt-8 md:mt-10">
  {error ? (
    <p className="text-red-500 text-center">{error}</p>
  ) : (
    <Swiper
      modules={[Navigation]}
      spaceBetween={10}
      slidesPerView={1}
      navigation
      breakpoints={{
        640: { slidesPerView: 2, spaceBetween: 15 },
        1024: { slidesPerView: 3, spaceBetween: 20 },
      }}
      loop={true}
      className="rounded-lg shadow-lg h-full" 
    >
      {apartmentImages.length > 0 ? (
        apartmentImages.map((src, index) => (
          <SwiperSlide key={index} className="h-full"> 
            <img 
              src={src} 
              alt={`Apartment ${index + 1}`} 
              className="w-full h-full object-cover" 
            />
          </SwiperSlide>
        ))
      ) : (
        <SwiperSlide className="h-full flex items-center justify-center"> {/* Added flex classes */}
          <p className="text-center text-gray-500">{t("no_images")}</p>
        </SwiperSlide>
      )}
    </Swiper>
  )}
  </div>

    {process.env.REACT_APP_ENV === "development" && (
      <>
        {/* PayPal SDK Status */}
        {paypalLoaded ? (
          <p className="text-green-500 text-center mt-4">{t("paypal_sdk_loaded")}</p>
        ) : (
          <p className="text-red-500 text-center mt-4">{t("paypal_sdk_not_loaded")}</p>
        )}
      </>
    )}

      <Footer />
    </div>
  );
};

export default HomePage;
